import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectActiveMessages,
  selectBlockingMessages,
  refreshMessageQueue
} from "./messageQueueSlice";
import { useCallback, useEffect } from "react";

const isTestSite = (): boolean =>
  typeof window !== "undefined" &&
  window.location.hostname === "platform-test.simpl.org";

export const useMessageQueue = () => {
  const dispatch = useAppDispatch();
  const activeMessages = useAppSelector(selectActiveMessages);
  const testSite = isTestSite();
  // Visibility: show_on_live / show_on_test (from Firebase) control which site shows each message.
  // Default: when a field is omitted (undefined), the message is shown on that site. So messages
  // without these fields appear on both live and test (backward compatible). We use !== false so
  // that both undefined and true mean "show"; only explicit false hides the message on that site.
  const messages = activeMessages.filter((msg) =>
    testSite ? msg.showOnTest !== false : msg.showOnLive !== false
  );
  const blocking = messages.filter((msg) => !msg.dismissible);
  const hasBlocking = blocking.length > 0;

  const refresh = useCallback(() => {
    dispatch(refreshMessageQueue());
  }, [dispatch]);

  useEffect(() => {
    refresh();

    const handleVisibility = () => {
      if (!document.hidden) refresh();
    };
    window.addEventListener("visibilitychange", handleVisibility);

    const interval = setInterval(refresh, 60 * 1000);
    return () => {
      window.removeEventListener("visibilitychange", handleVisibility);
      clearInterval(interval);
    };
  }, [refresh]);

  useEffect(() => {
    const detailedMessages = messages.map((msg) => ({
      id: msg.id,
      blocking: !msg.dismissible,
      dismissible: msg.dismissible,
      priority: msg.priority,
      scenario: msg.scenario,
      startTime: msg.startTime,
      endTime: msg.endTime,
      body: msg.body
    }));
    //console.log("[messageQueue] active messages", detailedMessages);
  }, [hasBlocking, messages]);

  return { messages, blocking, refresh, hasBlocking };
};


